/* MOJO PDP V2 (DEV). Vanilla web component, no dependencies.
   Reuses the Symmetry <product-form> for cart add (Ajax /cart/add, header + cart-drawer section refresh,
   'on:cart:add' / 'on:cart:error' events). Shopify standard analytics events (product_added_to_cart, etc.)
   are emitted by Shopify itself; this file only adds non-standard mojo_* interaction events. */
(() => {
  if (customElements.get('mojo-pdp')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = () => !!(navigator.connection && navigator.connection.saveData);

  const emit = (name, data = {}) => {
    const payload = { ...data };
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch (e) { /* noop */ }
    try { (window.dataLayer = window.dataLayer || []).push({ event: 'mojo_interaction', mojo_event: name, ...payload }); } catch (e) { /* noop */ }
  };

  class MojoPdp extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      document.body.classList.add('mojo-pdp-v2');
      try { this.data = JSON.parse(this.querySelector('[data-mojo-data]').textContent); } catch (e) { this.data = { variants: [] }; }
      this.form = this.querySelector('[data-mojo-form]');
      this.cta = this.querySelector('[data-mojo-cta]');
      this.base = { family: this.data.family, color: this.data.color, length: this.data.length };

      this.initGallery();
      this.initStage();
      this.initFilters();
      this.initNav();
      this.initViewTransitions();
      this.initTexture();
      this.initBuy();
      this.initBuyNow();
      this.initSticky();
      this.initSheets();
      this.initHelp();
      this.initDesktopSticky();
      // bfcache restore (back/forward): clear optimistic navigation feedback
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        this.querySelectorAll('.is-pending').forEach((x) => x.classList.remove('is-pending'));
        const next = this.querySelector('[data-mojo-stage-next]');
        if (next) { next.classList.remove('is-in'); next.hidden = true; }
        const name = this.querySelector('[data-mojo-stage-name]');
        if (name) name.textContent = this.data.color || name.textContent;
      });
    }

    /* ---------------- shipping estimate (cart rates, served by the Frenet carrier service) ----------------
       Runs only from the add-to-cart sheet, where the customer has already put this item in the cart, so the quote
       describes the real cart. Shopify's own endpoint is the single source: no carrier API, no credentials, no
       second cart, nothing added. Estimated days are printed only when the carrier returns them. */
    initShipping() {
      const box = this.querySelector('[data-mojo-ship]');
      if (!box) return;
      const input = box.querySelector('[data-mojo-cep]');
      const btn = box.querySelector('[data-mojo-cep-go]');
      const msg = box.querySelector('[data-mojo-ship-msg]');
      const list = box.querySelector('[data-mojo-ship-list]');
      const money = (v) => 'R$ ' + Number(v).toFixed(2).replace('.', ',');
      /* Shopify requires a province for Brazil, but the rate itself follows the CEP (verified: a Bahia CEP returns
         Bahia rates whatever province is sent). These are the official fixed CEP ranges per UF — static geography,
         not shipping logic. Frenet/Shopify remain the only source of prices and estimates. */
      const UF = [[1000,19999,'SP'],[20000,28999,'RJ'],[29000,29999,'ES'],[30000,39999,'MG'],[40000,48999,'BA'],
        [49000,49999,'SE'],[50000,56999,'PE'],[57000,57999,'AL'],[58000,58999,'PB'],[59000,59999,'RN'],
        [60000,63999,'CE'],[64000,64999,'PI'],[65000,65999,'MA'],[66000,68899,'PA'],[68900,68999,'AP'],
        [69000,69299,'AM'],[69300,69399,'RR'],[69400,69899,'AM'],[69900,69999,'AC'],[70000,72799,'DF'],
        [72800,72999,'GO'],[73000,73699,'DF'],[73700,76799,'GO'],[76800,76999,'RO'],[77000,77999,'TO'],
        [78000,78899,'MT'],[79000,79999,'MS'],[80000,87999,'PR'],[88000,89999,'SC'],[90000,99999,'RS']];
      const ufOf = (zip) => { const p = +zip.slice(0, 5); const hit = UF.find(([a2, b2]) => p >= a2 && p <= b2); return hit ? hit[2] : ''; };
      const eta = (r) => {
        const d = Array.isArray(r.delivery_days) ? r.delivery_days.filter((x) => Number.isFinite(x)) : [];
        if (!d.length) return '';
        const min = Math.min(...d); const max = Math.max(...d);
        return (min === max ? min : min + '–' + max) + ' ' + (max === 1 ? 'dia útil' : 'dias úteis');
      };
      input.addEventListener('input', () => {
        const v = input.value.replace(/\D/g, '').slice(0, 8);
        input.value = v.length > 5 ? v.slice(0, 5) + '-' + v.slice(5) : v;
      });
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } });
      btn.addEventListener('click', run);
      const self = this;
      async function run() {
        const zip = input.value.replace(/\D/g, '');
        list.innerHTML = '';
        if (zip.length !== 8) { msg.textContent = 'Digite um CEP com 8 números.'; return; }
        const uf = ufOf(zip);
        if (!uf) { msg.textContent = 'Não reconhecemos esse CEP. Confira o número.'; return; }
        btn.disabled = true; msg.textContent = 'Calculando…';
        try {
          const url = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') +
            'cart/shipping_rates.json?shipping_address%5Bcountry%5D=Brazil&shipping_address%5Bprovince%5D=' +
            encodeURIComponent(uf) + '&shipping_address%5Bzip%5D=' + encodeURIComponent(zip);
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          const data = await res.json();
          const rates = (data && data.shipping_rates) || [];
          if (!res.ok || !rates.length) {
            msg.textContent = 'Não encontramos opções de envio para este CEP. Confira o número ou finalize a compra para ver as opções.';
          } else {
            msg.textContent = '';
            list.innerHTML = rates.map((r) => {
              const e = eta(r);
              const price = Number(r.price) === 0 ? 'Grátis' : money(r.price);
              return '<li class="mojo-ship__opt"><span class="mojo-ship__opt-name">' + self.esc(r.name) + '</span>' +
                '<span class="mojo-ship__opt-meta">' + (e ? self.esc(e) + ' · ' : '') + price + '</span></li>';
            }).join('');
            try { window.Shopify && Shopify.analytics && Shopify.analytics.publish('mojo_shipping_estimate', { options: rates.length }); } catch (e) { /* noop */ }
          }
        } catch (err) {
          msg.textContent = 'Não foi possível calcular agora. Tente novamente.';
        }
        btn.disabled = false;
      }
    }

    esc(v) { return String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    /* ---------------- gallery ---------------- */
    initGallery() {
      const g = this.querySelector('[data-mojo-gallery]');
      if (!g) return;
      const track = g.querySelector('[data-mojo-track]');
      const slides = [...track.querySelectorAll('[data-mojo-slide]')];
      const dots = [...g.querySelectorAll('.mojo-gallery__dot')];
      const counter = g.querySelector('[data-mojo-counter-current]');
      const prev = g.querySelector('[data-mojo-prev]');
      const next = g.querySelector('[data-mojo-next]');
      let current = 0;
      let reported = false;

      const setCurrent = (i) => {
        if (i === current) return;
        slides[current]?.querySelectorAll('video').forEach((v) => v.pause());
        current = i;
        dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
        if (counter) counter.textContent = String(i + 1);
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === slides.length - 1;
        if (!reported) { reported = true; emit('mojo_gallery_interaction', { action: 'swipe', index: i + 1 }); }
      };
      if (prev) prev.disabled = true;

      const onScroll = () => {
        if (getComputedStyle(track).scrollSnapType === 'none') return;
        const left = track.scrollLeft;
        let best = 0; let bestD = Infinity;
        slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - track.offsetLeft - left - parseFloat(getComputedStyle(track).paddingLeft || 0));
          if (d < bestD) { bestD = d; best = i; }
        });
        setCurrent(best);
      };
      let raf = 0;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(onScroll); }, { passive: true });

      const go = (i) => {
        const s = slides[Math.max(0, Math.min(slides.length - 1, i))];
        track.scrollTo({ left: s.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      prev?.addEventListener('click', () => go(current - 1));
      next?.addEventListener('click', () => go(current + 1));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(current - 1); }
      });

      // Fullscreen viewer (images only)
      const viewer = this.querySelector('[data-mojo-viewer]');
      const vTrack = viewer?.querySelector('[data-mojo-viewer-track]');
      const zooms = [...g.querySelectorAll('[data-mojo-zoom]')];
      if (viewer && vTrack && typeof viewer.showModal === 'function') {
        g.addEventListener('click', (e) => {
          const z = e.target.closest('[data-mojo-zoom]');
          if (!z) return;
          if (!vTrack.childElementCount) {
            zooms.forEach((b) => {
              const img = document.createElement('img');
              img.src = b.dataset.full; img.alt = b.querySelector('img')?.alt || ''; img.loading = 'lazy'; img.decoding = 'async';
              vTrack.append(img);
            });
          }
          viewer.showModal();
          const idx = zooms.indexOf(z);
          requestAnimationFrame(() => { vTrack.scrollLeft = vTrack.clientWidth * idx; });
          emit('mojo_gallery_interaction', { action: 'fullscreen', index: idx + 1 });
        });
        viewer.querySelector('[data-mojo-viewer-close]').addEventListener('click', () => viewer.close());
        viewer.addEventListener('click', (e) => { if (e.target === viewer) viewer.close(); });
      }
    }

    /* ---------------- color stage ---------------- */
    initStage() {
      const stage = this.querySelector('[data-mojo-stage]');
      if (!stage) return;
      this.stage = stage;
      const video = stage.querySelector('[data-mojo-stage-video]');
      const toggle = stage.querySelector('[data-mojo-stage-toggle]');
      if (!video) return;
      let userPaused = false;
      let visible = false;
      let reported = false;
      const autoplayOk = () => !reducedMotion() && !saveData();

      const pickSrc = () => {
        if (video.getAttribute('src')) return;
        const isAppleWebKit = /Apple/.test(navigator.vendor || '') && !!video.canPlayType('application/vnd.apple.mpegurl');
        const src = (isAppleWebKit && video.dataset.hls) || video.dataset.mp4 || video.dataset.hls;
        if (src) video.src = src;
      };
      const play = () => {
        pickSrc();
        video.preload = 'auto';
        const p = video.play();
        if (p && p.then) {
          p.then(() => {
            stage.classList.add('is-playing');
            toggle?.setAttribute('aria-label', 'Pausar vídeo da cor');
            if (!reported) { reported = true; emit('mojo_color_stage_play', { color: this.data.color }); }
          }).catch(() => {
            // Autoplay refused (e.g. iOS low-power mode): drop the source so the poster shows again, not a dark first frame.
            if (video.paused && video.currentTime < 0.1) { video.removeAttribute('src'); video.load(); }
          });
        }
      };
      const pause = () => {
        if (video.getAttribute('src') && video.currentTime < 0.1) { video.removeAttribute('src'); video.load(); }
        video.pause();
        stage.classList.remove('is-playing');
        toggle?.setAttribute('aria-label', 'Reproduzir vídeo da cor');
      };

      // Start only after the page 'load' event so the video never competes with the LCP image.
      const whenLoaded = document.readyState === 'complete' ? Promise.resolve() : new Promise((r) => window.addEventListener('load', r, { once: true }));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          visible = entry.intersectionRatio >= 0.5;
          if (visible && !userPaused && autoplayOk()) whenLoaded.then(() => { if (visible && !userPaused) play(); });
          else if (!visible) pause();
        }, { threshold: [0, 0.5] }).observe(stage);
      }
      toggle?.addEventListener('click', () => {
        if (video.paused) { userPaused = false; play(); } else { userPaused = true; pause(); }
      });
      document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); });
    }

    /* ---------------- filters ---------------- */
    initFilters() {
      const chips = [...this.querySelectorAll('[data-mojo-filter]')];
      const grid = this.querySelector('[data-mojo-grid]');
      const status = this.querySelector('[data-mojo-grid-status]');
      if (!chips.length || !grid) return;
      const items = [...grid.querySelectorAll('.mojo-grid__item')];
      chips.forEach((chip) => chip.addEventListener('click', () => {
        const f = chip.dataset.mojoFilter;
        chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        let n = 0;
        items.forEach((it) => {
          const show = f === 'all' || it.dataset.group === f;
          it.hidden = !show;
          if (show) n += 1;
        });
        if (!reducedMotion()) {
          grid.classList.remove('is-reflow'); void grid.offsetWidth; grid.classList.add('is-reflow');
        }
        if (status) status.textContent = `${n} ${n === 1 ? 'cor' : 'cores'}`;
        emit('mojo_color_filter', { group: f, visible: n });
      }));
    }

    /* ---------------- sibling navigation (color / length) ---------------- */
    initNav() {
      // Preview only: keep the alternate template (?view=) on sibling links. No effect once the template is assigned.
      const view = new URLSearchParams(location.search).get('view');
      if (view) {
        this.viewParam = view;
        this.querySelectorAll('a[data-mojo-nav]').forEach((a) => { a.href = this.withView(a.getAttribute('href')); });
      }
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-mojo-nav]');
        if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const type = a.dataset.mojoNav;
        if (a.getAttribute('aria-current') === 'true') { e.preventDefault(); return; }

        // Immediate selection feedback while the sibling page loads
        a.closest('ul')?.querySelectorAll('.is-pending').forEach((x) => x.classList.remove('is-pending'));
        a.classList.add('is-pending');
        if (type === 'color' && this.stage && a.dataset.still) {
          const next = this.stage.querySelector('[data-mojo-stage-next]');
          const name = this.stage.querySelector('[data-mojo-stage-name]');
          const v = this.stage.querySelector('video');
          v?.pause();
          if (next) {
            next.hidden = false;
            next.onload = () => requestAnimationFrame(() => next.classList.add('is-in'));
            next.src = a.dataset.still;
          }
          if (name) name.textContent = a.dataset.name || '';
        }

        // Cross-document View Transition gate: only this exact sibling navigation may animate (see initViewTransitions)
        try { this.vtNav = { to: new URL(a.href, location.href).pathname, t: Date.now() }; } catch (err) { this.vtNav = null; }

        // Preserve the viewport position of the control group (restored by the inline script on arrival)
        const anchor = a.closest('[data-mojo-anchor]');
        try {
          const to = new URL(a.href, location.href);
          sessionStorage.setItem('mojo-pdp-nav', JSON.stringify({
            to: to.pathname, anchor: anchor ? anchor.dataset.mojoAnchor : '', top: anchor ? anchor.getBoundingClientRect().top : 0, t: Date.now()
          }));
        } catch (err) { /* private mode */ }

        emit(type === 'color' ? 'mojo_color_select' : 'mojo_length_select', {
          from: type === 'color' ? this.data.color : this.data.length,
          to: a.dataset.name || '',
          state: a.classList.contains('is-soldout') ? 'sold_out' : 'available'
        });
      });

      this.addEventListener('click', (e) => {
        const m = e.target.closest('[aria-disabled="true"].mojo-tile, [aria-disabled="true"].mojo-len, [aria-disabled="true"].mojo-tex');
        if (m) emit('mojo_option_unavailable_tap', { type: m.classList.contains('mojo-tile') ? 'color' : m.classList.contains('mojo-len') ? 'length' : 'texture', value: (m.querySelector('.mojo-tile__name, .mojo-len__cm, .mojo-tex__name') || {}).textContent || '' });
      });

      // Prefetch sibling pages on intent (pointer/touch), low priority
      const prefetched = new Set();
      const prefetch = (a) => {
        if (!a || prefetched.has(a.href) || saveData()) return;
        prefetched.add(a.href);
        const l = document.createElement('link');
        l.rel = 'prefetch'; l.href = a.href; l.as = 'document';
        document.head.append(l);
      };
      this.addEventListener('pointerenter', (e) => prefetch(e.target.closest?.('a[data-mojo-nav]:not([aria-current])')), true);
      this.addEventListener('touchstart', (e) => prefetch(e.target.closest?.('a[data-mojo-nav]:not([aria-current])')), { passive: true, capture: true });
    }

    /* ---------------- sibling View Transitions (progressive enhancement) ----------------
       CSS opts V2 pages in (@view-transition) and names only the configuration elements. Here every navigation that
       is NOT the colour/length sibling link just clicked (back/forward, reload, related products, header links) is
       skipped, so it behaves exactly as a normal navigation. Unsupported browsers never fire pageswap. */
    initViewTransitions() {
      window.addEventListener('pageswap', (e) => {
        if (!e.viewTransition) return;
        const nav = this.vtNav;
        let ok = false;
        try {
          ok = !!(nav && Date.now() - nav.t < 5000 && e.activation && e.activation.navigationType === 'push' &&
            new URL(e.activation.entry.url).pathname === nav.to);
        } catch (err) { ok = false; }
        if (!ok) { e.viewTransition.skipTransition(); return; }
        // mobile carousel: only capture the gallery if it is on screen (never fly an off-screen slide in)
        const g = this.querySelector('[data-mojo-gallery]');
        if (g) { const r = g.getBoundingClientRect(); if (r.bottom < 0 || r.top > innerHeight) g.style.viewTransitionName = 'none'; }
      });
    }

    /* ---------------- texture (variant on this page) ---------------- */
    initTexture() {
      const radios = [...this.querySelectorAll('.mojo-tex__input')];
      const select = this.querySelector('select[name="id"]');
      const onChange = (id) => {
        const v = this.data.variants.find((x) => String(x.id) === String(id));
        if (!v) return;
        this.texResolved = true;
        this.clearBuyNowHints();
        this.applyVariant(v);
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url.toString());
        emit('mojo_texture_select', { texture: v.tex, available: v.available });
      };
      radios.forEach((r) => r.addEventListener('change', () => onChange(r.value)));
      // Tapping the texture that is already selected fires no 'change', but she has still just
      // told us which one she wants — without this, "Comprar agora" would bounce her back to the
      // texture cards she just used.
      radios.forEach((r) => r.addEventListener('click', () => {
        if (!r.checked) return;
        this.texResolved = true;
        this.clearBuyNowHints();
      }));
      select?.addEventListener('change', () => onChange(select.value));
      const checked = radios.find((r) => r.checked);
      this.current = this.data.variants.find((x) => String(x.id) === String(checked ? checked.value : (select ? select.value : ''))) || this.data.variants[0];
    }

    applyVariant(v) {
      this.current = v;
      const roots = [this, this.stickyBar].filter(Boolean);
      const set = (sel, txt) => roots.forEach((r) => r.querySelectorAll(sel).forEach((n) => { n.textContent = txt; }));
      set('[data-mojo-price]', v.price);
      set('[data-mojo-summary-price]', v.price);
      set('[data-mojo-sticky-price]', v.price);
      if (v.inst) set('[data-mojo-inst]', v.inst);
      // payment values (verified checkout table) live in the page and in the payment sheet (moved to <body>)
      const setAll = (sel, txt) => { if (txt) document.querySelectorAll(sel).forEach((n) => { n.textContent = txt; }); };
      setAll('[data-mojo-card-inst]', v.cardInst);
      setAll('[data-mojo-card-total]', v.cardTotal);
      setAll('[data-mojo-pix-inst]', v.pixInst);
      setAll('[data-mojo-price-1x]', v.price1x);
      const bn = this.querySelector('[data-mojo-buy-now]');
      if (bn) {
        bn.dataset.state = v.available ? 'ready' : 'unavailable';
        const bl = bn.querySelector('[data-mojo-buy-now-label]');
        if (bl) bl.textContent = v.available ? 'Comprar agora' : 'Ver opções disponíveis';
      }
      document.querySelectorAll('[data-mojo-paysheet] [data-mojo-inst]').forEach((n) => { if (v.inst) n.textContent = v.inst; });
      const cmp = this.querySelector('[data-mojo-compare]');
      if (cmp) {
        cmp.hidden = !v.compare;
        cmp.innerHTML = v.compare ? `<span class="visually-hidden">Preço original: </span>${v.compare}` : '';
      }
      if (v.tex) { set('[data-mojo-summary-tex]', v.tex); set('[data-mojo-sticky-tex]', v.tex); }

      const label = this.querySelector('[data-mojo-cta-label]');
      if (this.cta) {
        this.cta.disabled = !v.available;
        if (v.available) this.cta.removeAttribute('aria-disabled'); else this.cta.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = v.available ? 'Adicionar ao carrinho' : 'Esgotado';
      }
      const sBtn = this.stickyBar && this.stickyBar.querySelector('[data-mojo-sticky-add]');
      const sLbl = this.stickyBar && this.stickyBar.querySelector('[data-mojo-sticky-label]');
      if (sBtn) sBtn.disabled = !v.available;
      if (sLbl) sLbl.textContent = v.available ? 'Adicionar' : 'Esgotado';

      // Keep sibling links on the selected texture (exact variant when that sibling has it)
      this.querySelectorAll('a[data-mojo-nav]').forEach((a) => {
        let map = {};
        try { map = JSON.parse(a.dataset.texUrls || '{}'); } catch (e) { /* ignore */ }
        a.href = this.withView(map[v.tex] || a.dataset.base || a.getAttribute('href'));
      });
    }

    withView(href) {
      if (!this.viewParam || !href) return href;
      const u = new URL(href, location.href);
      u.searchParams.set('view', this.viewParam);
      return u.pathname + u.search;
    }

    /* ---------------- buy / add to cart ---------------- */
    initBuy() {
      // Use the MOJO confirmation sheet instead of the theme drawer/notification on this template only.
      if (window.theme && window.theme.settings) window.theme.settings.afterAddToCart = 'mojo-sheet';
      this.atcSource = 'main';
      this.addEventListener('on:cart:add', (e) => {
        const id = e.detail && e.detail.variantId;
        const v = this.data.variants.find((x) => String(x.id) === String(id)) || this.current;
        if (this.atcSource === 'buy_now') {
          this.atcSource = 'main';
          this.continueToCheckout('buy_now');
          return;
        }
        this.cta?.classList.add('is-success');
        setTimeout(() => this.cta?.classList.remove('is-success'), 1200);
        this.openAddedSheet(v);
        emit(this.atcSource === 'sticky' ? 'mojo_sticky_add_to_cart' : 'mojo_add_to_cart', { variant_id: v ? v.id : id, texture: v ? v.tex : '' });
        this.atcSource = 'main';
        this.stickyBtnLoading(false);
      });
      this.addEventListener('on:cart:error', (e) => {
        this.stickyBtnLoading(false);
        this.buyNowLoading(false);
        const err = this.querySelector('.js-form-error');
        if (err && !err.hidden) {
          err.setAttribute('tabindex', '-1');
          if (this.atcSource === 'sticky') err.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
          err.focus({ preventScroll: this.atcSource !== 'sticky' });
        }
        emit('mojo_atc_error', { message: (e.detail && e.detail.error) || '' });
        this.atcSource = 'main';
      });
    }

    openAddedSheet(v) {
      const sheet = this.addedSheet;
      if (!sheet || typeof sheet.showModal !== 'function') return;
      const parts = [this.data.color, this.data.length, v && v.tex].filter(Boolean);
      sheet.querySelector('[data-mojo-sheet-config]').textContent = parts.join(' · ');
      sheet.querySelector('[data-mojo-sheet-price]').textContent = v ? v.price : '';
      const thumb = sheet.querySelector('[data-mojo-sheet-thumb]');
      if (thumb && !thumb.childElementCount && this.thumbSrc) {
        const img = document.createElement('img'); img.src = this.thumbSrc; img.alt = ''; thumb.append(img);
      }
      if (!sheet.open) sheet.showModal();
      sheet.querySelector('[data-mojo-checkout]')?.focus();
    }

    /* ---------------- early "Comprar agora" ---------------- */
    initBuyNow() {
      this.texResolved = !!this.data.texResolved;
      const btn = this.querySelector('[data-mojo-buy-now]');
      if (!btn) return;
      btn.addEventListener('click', async () => {
        const v = this.current;
        const hasTexChoice = !!this.querySelector('.mojo-tex__input');
        // 1. unresolved texture -> go to texture (never guess)
        if (hasTexChoice && !this.texResolved) {
          this.goToChoice('[data-mojo-texture]', '[data-mojo-tex-hint]', 'Escolha a textura para continuar.');
          emit('mojo_buy_now_click', { outcome: 'redirect_config', missing: 'texture' });
          return;
        }
        // 2. exact variant unavailable -> show it, go to configuration (never switch silently)
        if (!v || !v.available) {
          this.goToChoice(hasTexChoice ? '[data-mojo-texture]' : '[data-mojo-anchor="config"]', null, 'Esta combinação está esgotada. Veja as opções disponíveis.');
          emit('mojo_buy_now_click', { outcome: 'unavailable', variant_id: v ? v.id : null });
          return;
        }
        // 3. complete + available -> exact variant to cart (theme product-form) -> checkout
        emit('mojo_buy_now_click', { outcome: 'proceed', variant_id: v.id });
        this.buyNowLoading(true);
        try {
          const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          if (cart.items && cart.items.some((i) => String(i.variant_id) === String(v.id))) { this.continueToCheckout(); return; }
        } catch (e) { /* fall through to add */ }
        this.atcSource = 'buy_now';
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.cta); else this.cta.click();
      });
    }

    /* The cart drawer's terms checkbox sits between "Comprar agora" and payment: a whole
       screen and two extra taps after the customer has already decided.
       directCheckout does not drop that agreement — it moves it. The same terms text and
       link are printed inline next to the buy buttons (.mojo-consent), so acceptance
       happens at the moment of the decision rather than on a separate screen. The shortcut
       only applies while that inline notice is actually rendered; without it the cart
       drawer keeps its veto, which is also the default. */
    continueToCheckout(from) {
      const consentInline = this.data.directCheckout && this.querySelector('[data-mojo-consent]');
      if (!consentInline) {
        const terms = document.querySelector('terms-agreement');
        const drawer = document.querySelector('.js-cart-drawer');
        if (terms && !terms.querySelector('input:checked') && drawer && typeof drawer.open === 'function') {
          this.buyNowLoading(false);
          drawer.open(this.querySelector('[data-mojo-buy-now]'));
          emit('mojo_checkout_via_cart_terms', { from: from || 'buy_now' });
          return;
        }
      }
      emit('mojo_checkout_start', { from: from || 'buy_now' });
      window.location.href = this.data.checkoutUrl || '/checkout';
    }

    goToChoice(targetSel, hintSel, message) {
      const target = this.querySelector(targetSel);
      const msg = this.querySelector('[data-mojo-buy-now-msg]');
      if (msg) { msg.textContent = message; msg.hidden = false; }
      if (hintSel) { const h = this.querySelector(hintSel); if (h) { h.textContent = message; h.hidden = false; } }
      if (target) {
        target.classList.add('is-attention');
        const header = document.querySelector('.pageheader--sticky, .section-header');
        const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: reducedMotion() ? 'auto' : 'smooth' });
        const focusable = target.querySelector('input:not([disabled]), a, button');
        setTimeout(() => focusable?.focus({ preventScroll: true }), reducedMotion() ? 0 : 450);
      }
    }

    clearBuyNowHints() {
      this.querySelectorAll('[data-mojo-tex-hint], [data-mojo-buy-now-msg]').forEach((n) => { n.hidden = true; });
      this.querySelectorAll('.is-attention').forEach((n) => n.classList.remove('is-attention'));
    }

    buyNowLoading(on) {
      const b = this.querySelector('[data-mojo-buy-now]');
      if (!b) return;
      b.classList.toggle('is-loading', on);
      b.setAttribute('aria-busy', String(on));
    }

    /* ---------------- sticky purchase bar ---------------- */
    initSticky() {
      const bar = this.querySelector('[data-mojo-sticky]');
      if (!bar || !this.cta) return;
      document.body.append(bar); // escape transformed ancestors so position:fixed is viewport-relative
      this.stickyBar = bar;
      const current = this.querySelector('.mojo-tile[aria-current="true"] img') || this.querySelector('.mojo-gallery__img');
      this.thumbSrc = current ? (current.currentSrc || current.src) : '';
      const slot = bar.querySelector('[data-mojo-thumb-slot]');
      if (slot && this.thumbSrc) { const img = document.createElement('img'); img.src = this.thumbSrc; img.alt = ''; img.loading = 'lazy'; slot.append(img); }

      let ctaPassed = false; let footerIn = false; let shown = false; let reported = false;
      const update = () => {
        const show = ctaPassed && !footerIn;
        if (show === shown) return;
        shown = show;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
        bar.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('mojo-has-sticky', show);
        if (show && !reported) { reported = true; emit('mojo_sticky_shown'); }
      };
      // Position check on scroll (rAF-throttled). An IntersectionObserver alone misses jumps that skip over the CTA
      // (anchor links, fast flings), so the rects are read directly.
      const footer = document.querySelector('.section-footer, footer, #shopify-section-footer');
      let ticking = false;
      const measure = () => {
        ticking = false;
        ctaPassed = this.cta.getBoundingClientRect().bottom < 0;
        footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        update();
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(measure); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      measure();

      bar.querySelector('[data-mojo-sticky-add]')?.addEventListener('click', () => {
        if (!this.form || !this.cta || this.cta.disabled) return;
        this.atcSource = 'sticky';
        this.stickyBtnLoading(true);
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.cta); else this.cta.click();
      });
    }

    stickyBtnLoading(on) {
      const b = this.stickyBar && this.stickyBar.querySelector('[data-mojo-sticky-add]');
      if (!b) return;
      b.classList.toggle('is-loading', on);
      b.setAttribute('aria-busy', String(on));
    }

    /* ---------------- sheets (added + payment) ---------------- */
    initSheets() {
      this.addedSheet = this.querySelector('[data-mojo-sheet]');
      this.initShipping();
      this.paySheet = this.querySelector('[data-mojo-paysheet]');
      this.sizeSheet = this.querySelector('[data-mojo-sizesheet]');
      const sheets = [this.addedSheet, this.paySheet, this.sizeSheet].filter(Boolean);
      sheets.forEach((s) => {
        document.body.append(s);
        s.addEventListener('click', (e) => {
          if (e.target === s || e.target.closest('[data-mojo-sheet-close]')) s.close();
        });
      });
      const added = sheets.find((s) => s.matches('[data-mojo-sheet]'));
      added?.querySelector('[data-mojo-view-cart]')?.addEventListener('click', (e) => {
        const drawer = document.querySelector('.js-cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          e.preventDefault();
          added.close();
          drawer.open(e.currentTarget);
        }
      });
      // Checkout handoff and the store's cart terms step (<terms-agreement> in the cart drawer).
      // Default: unchanged — while the terms are not accepted, "Finalizar compra" opens the cart
      // drawer, where terms + checkout live.
      // When the section turns on directCheckout, the terms are NOT dropped: the same agreement is
      // stated and linked inline next to the buy buttons (.mojo-consent), so the customer accepts at
      // the moment of the decision instead of on a separate screen. The inline notice must be present
      // for the shortcut to apply — no notice, no shortcut.
      added?.querySelector('[data-mojo-checkout]')?.addEventListener('click', (e) => {
        const consentInline = this.data.directCheckout && this.querySelector('[data-mojo-consent]');
        const terms = document.querySelector('terms-agreement');
        const drawer = document.querySelector('.js-cart-drawer');
        if (!consentInline && terms && !terms.querySelector('input:checked') && drawer && typeof drawer.open === 'function') {
          e.preventDefault();
          e.stopPropagation();
          added.close();
          drawer.open(e.currentTarget);
          emit('mojo_checkout_via_cart_terms');
          return;
        }
        emit('mojo_checkout_start', { from: 'atc_sheet' });
        // Drive the handoff ourselves rather than leaning on the link's default action.
        // Much of MOJO's traffic arrives in Instagram/WhatsApp webviews, where a default
        // anchor activation inside an open <dialog> is not reliable.
        const href = e.currentTarget.getAttribute('href') || this.data.checkoutUrl || '/checkout';
        e.preventDefault();
        added.close();
        window.location.href = href;
      }, true);
      // Size guide: on-page sheet/modal; "Ver 45cm" reuses the exact sibling link of the length card (texture kept).
      if (this.sizeSheet) {
        this.querySelectorAll('[data-mojo-size-open]').forEach((b) => b.addEventListener('click', () => {
          this.sizeSheet.querySelectorAll('[data-len]').forEach((row) => {
            const go = row.querySelector('[data-mojo-size-go]');
            const card = [...this.querySelectorAll('a.mojo-len')].find((a) => (a.dataset.name || '').toLowerCase() === row.dataset.len.toLowerCase());
            if (go) { go.hidden = !card; if (card) go.href = card.getAttribute('href'); }
          });
          this.sizeSheet.showModal();
          emit('mojo_size_help', { family: this.data.family, length: this.data.length, surface: 'size_guide' });
        }));
      }
      const pay = sheets.find((s) => s.matches('[data-mojo-paysheet]'));
      if (pay) {
        this.querySelectorAll('[data-mojo-pay-open]').forEach((b) => b.addEventListener('click', () => {
          pay.showModal();
          emit('mojo_payment_details_open', { from: b.closest('.mojo-support') ? 'purchase' : 'price' });
        }));
      }
    }


    /* ---------------- help links ---------------- */
    initHelp() {
      this.addEventListener('click', (e) => {
        const h = e.target.closest('[data-mojo-help]');
        if (h) emit(h.dataset.mojoHelp === 'color' ? 'mojo_color_help' : 'mojo_size_help', { family: this.data.family, color: this.data.color });
      });
    }

    /* ---------------- desktop: keep the tall purchase column usable while the gallery scrolls ---------------- */
    initDesktopSticky() {
      const info = this.querySelector('[data-mojo-info]');
      if (!info) return;
      const header = document.querySelector('.pageheader--sticky');
      const set = () => {
        const headerH = header ? header.getBoundingClientRect().height : 0;
        const h = info.getBoundingClientRect().height;
        const top = Math.min(headerH + 24, window.innerHeight - h - 24);
        info.style.setProperty('--mojo-sticky-top', `${top}px`);
      };
      set();
      window.addEventListener('resize', set, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(set).observe(info);
    }
  }

  customElements.define('mojo-pdp', MojoPdp);
})();
